import { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Button, Flex } from '@rover/kibble/core';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { AvatarType, Size } from '../../Avatar/Avatar';
import StackedAvatar from '../../StackedAvatar/StackedAvatar';
import { ShortlistOpenEvent } from '../analyticsEvents';
import { MAX_PILL_AVATARS } from '../constants';
import useShortlist from '../hooks/useShortlist';
import { useShortlistPreviewTarget } from '../ShortlistPreviewTargetContext';

import type { ShortlistPreviewVariantProps } from './ShortlistPreview';

const pulseAnimationStyles = {
  '@keyframes shortlistPillPulse': {
    '0%': { transform: 'scale(1)' },
    '30%': { transform: 'scale(1.05)' },
    '60%': { transform: 'scale(0.98)' },
    '100%': { transform: 'scale(1)' },
  },
  animation: 'shortlistPillPulse 0.4s ease-in-out',
};

/**
 * The mWeb contact-list preview: a floating pill anchored bottom-right showing
 * the added sitters' avatars (up to {@link MAX_PILL_AVATARS}) and the real
 * count. Renders nothing while the shortlist is empty.
 */
const ShortlistPreviewPill = ({ focusProps }: ShortlistPreviewVariantProps): JSX.Element | null => {
  const prefersReducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)');
  const { count, getSitters, setIsModalVisible } = useShortlist();
  const { registerTarget } = useShortlistPreviewTarget();
  const [isAnimating, setIsAnimating] = useState(false);

  const prevCount = useRef(count);
  useEffect(() => {
    if (count > prevCount.current && !prefersReducedMotion) {
      setIsAnimating(true);
    }
    prevCount.current = count;
  }, [count, prefersReducedMotion]);

  if (count === 0) {
    return null;
  }

  const imageUrls = getSitters()
    .map((sitter) => sitter.defaultImage.medium || sitter.defaultImage.small)
    .filter(Boolean)
    .slice(0, MAX_PILL_AVATARS);

  const handlePress = (): void => {
    emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'preview', count }));
    setIsModalVisible(true);
  };

  return (
    <Box
      position="fixed"
      right="4x"
      bottom="6x"
      // Same layer as the fixed Filters button (ADVANCED_FILTER_BUTTON_FIXED =
      // NAVBAR_FIXED); the pill is SearchPage's last child, so on a tie it
      // renders above — the primary CTA must win any overlap on tiny viewports.
      zIndex={String(ZIndex.NAVBAR_FIXED.toNumber())}
      testID="shortlist-preview-pill"
      sx={isAnimating ? pulseAnimationStyles : {}}
      webProps={{ onAnimationEnd: () => setIsAnimating(false), ...focusProps }}
    >
      <Button
        variant="primary"
        onPress={handlePress}
        sx={{ borderRadius: 'round', boxShadow: 'elevation_mid' }}
      >
        <Flex flexDirection="row" alignItems="center" gap="3x">
          {/* Decorative: the button's visible label already states the count. */}
          <Box
            ref={registerTarget}
            display="inline-flex"
            flexShrink="0"
            webProps={{ 'aria-hidden': true }}
          >
            <StackedAvatar
              imageUrls={imageUrls}
              avatarType={AvatarType.Person}
              size={Size.ExtraSmall}
            />
          </Box>
          <Trans>View contact list ({count})</Trans>
        </Flex>
      </Button>
    </Box>
  );
};

export default ShortlistPreviewPill;
